import { notifications } from '@mantine/notifications';
import { getChannel, Channel } from '@ayocore/platform/messaging/client';
import { AuthController } from '@ayocore/platform/identity/client/AuthController';
import type { AuthControllerState } from '@ayocore/platform/identity/client/AuthController';
import { NotificationMessageSchema, NotificationMessage } from '../common/types';
import {
  useNotificationRepository,
  getVapidPublicKey as getVapidPublicKeyRpc,
} from '@ayocore/platform/notifications/client/rpc';
import { WebPushController } from './services/WebPushController';
import type { Notification } from '../schemas/notification.schema';
import logger from '@ayocore/platform/common/helpers/logger';
import { ensureServiceWorkerRegistration } from '@ayocore/platform/client/pwa/registerServiceWorker';

/**
 * NotificationController - Clean Singleton for Global Notifications
 *
 * Follows the same pattern AuthController:
 * - External state management (not React state)
 * - Simple callback subscription system
 * - Self-initializing with auth state monitoring
 * - Always-on WebSocket connection when authenticated
 * - Push notification management
 */

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  // Push notification state
  pushEnabled: boolean;
  pushPermission: NotificationPermission;
  pushSupported: boolean;
  pushLoading: boolean;
}

const notificationRepository = useNotificationRepository();

export class NotificationController {
  private static instance: NotificationController;

  // External state (not React state)
  private state: NotificationState = {
    notifications: [],
    unreadCount: 0,
    isLoading: false,
    error: null,
    // Push notification state
    pushEnabled: false,
    pushPermission: 'default',
    pushSupported: false,
    pushLoading: false,
  };

  // Simple callback system (no RxJS)
  private listeners: Set<() => void> = new Set();
  private authController = AuthController.getInstance();
  private channel: Channel<unknown, NotificationMessage> | null = null;
  private pushController = WebPushController.getInstance();

  private constructor() {
    // Initialize push state
    this.initializePushState();

    // Убеждаемся, что Service Worker уже зарегистрирован (инициализация PWA)
    void ensureServiceWorkerRegistration();

    // Subscribe to auth changes - setup/cleanup WebSocket
    this.authController.subscribe(() => {
      const authState = this.authController.getSnapshot();
      this.handleAuthChange(authState);
    });

    // Initialize immediately if already authenticated
    const currentAuth = this.authController.getSnapshot();
    if (currentAuth.isAuthenticated && currentAuth.user) {
      this.setupWebSocket();
      this.loadNotifications(currentAuth.user.id);
    }
  }

  static getInstance(): NotificationController {
    if (!NotificationController.instance) {
      NotificationController.instance = new NotificationController();
    }
    return NotificationController.instance;
  }

  // Subscription system for React integration
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // Get current state snapshot
  // Важно: useSyncExternalStore требует референциальной стабильности снапшота,
  // поэтому возвращаем тот же объект состояния без копирования.
  getSnapshot = (): NotificationState => this.state;

  // Notify all listeners of state changes
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        logger.error('Error in notification listener:', error);
      }
    });
  }

  // Handle auth state changes
  private handleAuthChange(authState: AuthControllerState): void {
    if (authState.isAuthenticated && authState.user) {
      this.setupWebSocket();
      this.loadNotifications(authState.user.id);
    } else {
      this.cleanup();
    }
  }

  // Helper: immutable state replace
  private replaceState(patch: Partial<NotificationState>): void {
    this.state = { ...this.state, ...patch };
  }

  // Setup WebSocket connection (always-on when authenticated)
  private setupWebSocket(): void {
    if (this.channel) {
      return; // Already set up
    }

    try {
      this.channel = getChannel('notifications').withSchema(NotificationMessageSchema);

      // Listen for real-time notifications
      this.channel.onMessage((message: NotificationMessage) => {
        logger.info('🔔 [NotificationController] Received WebSocket message:', message);
        try {
          const notification = this.convertMessageToNotification(message);
          logger.info('🔔 [NotificationController] Converted to notification:', notification);
          this.addNotification(notification);
        } catch (error) {
          logger.error('Error processing notification message:', error);
        }
      });

      // Handle connection events
      this.channel.onConnectionStart(() => {
        this.clearError();
      });

      this.channel.onConnectionLost(() => {
        this.setError('Connection lost. Notifications may be delayed.');
      });

      this.channel.onError((error: Error) => {
        this.setError(`Notification system error: ${error.message}`);
      });
    } catch (error) {
      logger.error('Failed to setup WebSocket:', error);
      this.setError('Failed to connect to notification system');
    }
  }

  // Load initial notifications from database
  private async loadNotifications(userId: number): Promise<void> {
    try {
      this.replaceState({ isLoading: true });
      this.clearError();
      this.notifyListeners();

      const data = await notificationRepository
        .where('user_id', '=', userId)
        .orderBy('created_at', 'desc')
        .limit(50);

      const notifications = Array.isArray(data) ? data : [];
      const unreadCount = notifications.filter(n => !n.is_read).length;
      this.replaceState({ notifications, unreadCount });
    } catch (error) {
      logger.error('Failed to load notifications:', error);
      this.setError('Failed to load notifications');
      this.replaceState({ notifications: [], unreadCount: 0 });
    } finally {
      this.replaceState({ isLoading: false });
      this.notifyListeners();
    }
  }

  // Convert WebSocket message to database notification format
  private convertMessageToNotification(message: NotificationMessage): Notification {
    return {
      id: message.id,
      uuid: '', // Will be populated when loaded from DB
      title: message.title,
      message: message.message,
      type: message.type as 'info' | 'success' | 'warning' | 'error',
      user_id: message.user_id,
      is_read: message.is_read,
      created_at: new Date(message.created_at), // Convert string to Date
      updated_at: new Date(message.created_at), // Use same timestamp
      entity_uuid: null,
      entity_type: null,
      entity_url: message.entity_url ?? null,
      payload: message.payload ?? null,
      user: null, // Required for relation pattern
      sort_order: null,
      push_sent: false,
    };
  }

  // Add new notification (from real-time or refresh)
  private addNotification(notification: Notification): void {
    logger.info('🔔 [NotificationController] Adding notification:', notification);

    // Check if notification already exists
    const existingIndex = this.state.notifications.findIndex(n => n.id === notification.id);

    if (existingIndex >= 0) {
      logger.info('🔔 [NotificationController] Notification already exists, checking if unread');
      // Notification exists - check if it's unread and show popup
      const existing = this.state.notifications[existingIndex];
      if (!existing.is_read && !notification.is_read) {
        // Show popup for unread notification that came via WebSocket
        logger.info('🔔 [NotificationController] Showing popup for existing unread notification');
        this.showPopupNotification(notification);
      }
      return;
    }

    logger.info('🔔 [NotificationController] Adding new notification to state');
    // Add to top of list, keep max 50
    const notifications = [notification, ...this.state.notifications.slice(0, 49)];
    const unreadCount = notifications.filter(n => !n.is_read).length;
    this.replaceState({ notifications, unreadCount });
    this.notifyListeners();

    // Show popup notification for real-time messages only
    logger.info('🔔 [NotificationController] Showing popup for new notification');
    this.showPopupNotification(notification);
  }

  // Show popup notification with enhanced styling
  private showPopupNotification(notification: Notification): void {
    logger.info('🔔 [NotificationController] Attempting to show popup for:', notification);
    try {
      const color = this.getNotificationColor(notification.type);
      const icon = this.getNotificationIcon(notification.type);

      logger.info('🔔 [NotificationController] Calling notifications.show with:', {
        id: `notification-${notification.id}`,
        title: `🔔 ${notification.title}`,
        message: notification.message,
        color: color,
        icon: icon,
      });

      notifications.show({
        id: `notification-${notification.id}`,
        title: `🔔 ${notification.title}`,
        message: notification.message,
        color: color,
        icon: icon,
        autoClose: 8000,
        position: 'top-right',
        withCloseButton: true,
        withBorder: true,
        radius: 'md',
        style: {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          animation: 'slideInRight 0.3s ease-out',
        },
      });

      logger.info('🔔 [NotificationController] Popup notification called successfully');

      // Play audio notification
      this.playNotificationSound(notification.type);
    } catch (error) {
      logger.error('Failed to show popup notification:', error);
    }
  }

  // Play notification sound based on type
  private playNotificationSound(type: string): void {
    try {
      // Create audio context for different notification types
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different frequencies for different notification types
      switch (type) {
        case 'success':
          oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
          break;
        case 'warning':
          oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(500, audioContext.currentTime + 0.1);
          break;
        case 'error':
          oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
          oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1);
          break;
        case 'info':
        default:
          oscillator.frequency.setValueAtTime(700, audioContext.currentTime);
          break;
      }

      // Gentle volume and duration
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      // Silently fail if audio is not supported or blocked
      logger.debug('Audio notification not available:', error);
    }
  }

  // Get notification icon based on type
  private getNotificationIcon(type: string): string {
    switch (type) {
      case 'success':
        return '✅';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      case 'info':
      default:
        return '💡';
    }
  }

  // Update unread count
  private updateUnreadCount(): void {
    const unreadCount = this.state.notifications.filter(n => !n.is_read).length;
    this.replaceState({ unreadCount });
  }

  // Set error state
  private setError(error: string): void {
    this.replaceState({ error });
    this.notifyListeners();
  }

  // Clear error state
  private clearError(): void {
    if (this.state.error) {
      this.replaceState({ error: null });
      this.notifyListeners();
    }
  }

  // Clean up when user logs out
  private cleanup(): void {
    this.replaceState({ notifications: [], unreadCount: 0, isLoading: false, error: null });
    this.channel = null;
    this.notifyListeners();
  }

  // Get notification color for toasts
  private getNotificationColor(type: string): string {
    switch (type) {
      case 'success':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'error':
        return 'red';
      case 'info':
      default:
        return 'blue';
    }
  }

  // Public API methods

  /**
   * Get all notifications
   */
  getNotifications(): Notification[] {
    return [...this.state.notifications];
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): number {
    return this.state.unreadCount;
  }

  /**
   * Check if loading
   */
  isLoading(): boolean {
    return this.state.isLoading;
  }

  /**
   * Get current error
   */
  getError(): string | null {
    return this.state.error;
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: number): Promise<void> {
    try {
      await notificationRepository.update(notificationId, { is_read: true });

      // Update local state
      const notifications = this.state.notifications.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      );
      const unreadCount = notifications.filter(n => !n.is_read).length;
      this.replaceState({ notifications, unreadCount });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
      this.setError('Failed to mark notification as read');
      throw error;
    }
  }

  /**
   * Delete notification
   */
  async deleteNotification(notificationId: number): Promise<void> {
    try {
      await notificationRepository.deleteById(notificationId);

      // Update local state
      const notifications = this.state.notifications.filter(n => n.id !== notificationId);
      const unreadCount = notifications.filter(n => !n.is_read).length;
      this.replaceState({ notifications, unreadCount });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to delete notification:', error);
      this.setError('Failed to delete notification');
      throw error;
    }
  }

  /**
   * Refresh notifications from server
   */
  async refreshNotifications(): Promise<void> {
    const authState = this.authController.getSnapshot();
    if (authState.isAuthenticated && authState.user) {
      await this.loadNotifications(authState.user.id);
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<void> {
    try {
      const unreadIds = this.state.notifications.filter(n => !n.is_read).map(n => n.id);

      if (unreadIds.length === 0) return;

      // Update all unread notifications
      await Promise.all(unreadIds.map(id => notificationRepository.update(id, { is_read: true })));

      // Update local state
      const notifications = this.state.notifications.map(n => ({ ...n, is_read: true }));
      this.replaceState({ notifications, unreadCount: 0 });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to mark all notifications as read:', error);
      this.setError('Failed to mark all notifications as read');
      throw error;
    }
  }

  /**
   * Delete all notifications
   */
  async deleteAllNotifications(): Promise<void> {
    try {
      await notificationRepository.deleteAll();

      // Update local state
      this.replaceState({ notifications: [], unreadCount: 0 });
      this.notifyListeners();
    } catch (error) {
      logger.error('Failed to delete all notifications:', error);
      this.setError('Failed to delete all notifications');
      throw error;
    }
  }

  // ===== PUSH NOTIFICATION METHODS =====

  /**
   * Initialize push notification state
   */
  private initializePushState(): void {
    const supported = this.pushController.isSupported();
    const permission = this.pushController.getPermission();

    this.replaceState({
      pushSupported: supported,
      pushPermission: permission,
      pushEnabled: supported && permission === 'granted',
    });

    logger.info(
      `🔔 [NotificationController] Push state initialized: supported=${supported}, permission=${permission}`
    );
  }

  /**
   * Enable push notifications
   */
  async enablePush(): Promise<boolean> {
    if (!this.state.pushSupported) {
      logger.warn('⚠️ [NotificationController] Push notifications not supported');
      return false;
    }

    try {
      this.replaceState({ pushLoading: true });
      this.notifyListeners();

      // Get VAPID public key from server
      const vapidPublicKey = await this.getVapidPublicKey();
      if (!vapidPublicKey) {
        logger.error('❌ [NotificationController] VAPID public key not available');
        this.replaceState({ pushLoading: false });
        this.notifyListeners();
        return false;
      }

      // Enable push through WebPushController
      const success = await this.pushController.enablePush(vapidPublicKey);

      if (success) {
        this.replaceState({
          pushEnabled: true,
          pushPermission: 'granted',
          pushLoading: false,
        });
        logger.info('✅ [NotificationController] Push notifications enabled');
      } else {
        this.replaceState({ pushLoading: false });
        logger.warn('⚠️ [NotificationController] Failed to enable push notifications');
      }

      this.notifyListeners();
      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error enabling push:', error);
      this.replaceState({ pushLoading: false });
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Disable push notifications
   */
  async disablePush(): Promise<boolean> {
    try {
      this.replaceState({ pushLoading: true });
      this.notifyListeners();

      const success = await this.pushController.disablePush();

      if (success) {
        this.replaceState({
          pushEnabled: false,
          pushLoading: false,
        });
        logger.info('✅ [NotificationController] Push notifications disabled');
      } else {
        this.replaceState({ pushLoading: false });
        logger.warn('⚠️ [NotificationController] Failed to disable push notifications');
      }

      this.notifyListeners();
      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error disabling push:', error);
      this.replaceState({ pushLoading: false });
      this.notifyListeners();
      return false;
    }
  }

  /**
   * Get push notification status
   */
  async getPushStatus(): Promise<{
    supported: boolean;
    enabled: boolean;
    permission: NotificationPermission;
    loading: boolean;
  }> {
    return {
      supported: this.state.pushSupported,
      enabled: this.state.pushEnabled,
      permission: this.state.pushPermission,
      loading: this.state.pushLoading,
    };
  }

  /**
   * Send test push notification
   */
  async sendTestPush(): Promise<boolean> {
    try {
      const success = await this.pushController.sendTestPush();

      // Оставляем только одно место логирования об успехе/ошибке
      logger.info(
        `🔔 [NotificationController] Test push result: ${success ? 'success' : 'failed'}`
      );

      return success;
    } catch (error) {
      logger.error('❌ [NotificationController] Error sending test push:', error);
      return false;
    }
  }

  /**
   * Получает публичный ключ VAPID с сервера через RPC (notifications::getVapidPublicKey).
   */
  private async getVapidPublicKey(): Promise<string | null> {
    try {
      const publicKey = await getVapidPublicKeyRpc();
      if (!publicKey) {
        logger.warn('⚠️ [NotificationController] VAPID public key not available from server');
        return null;
      }
      logger.info('✅ [NotificationController] VAPID public key retrieved from server');
      return publicKey;
    } catch (error) {
      logger.error('❌ [NotificationController] Failed to get VAPID public key:', error);
      return null;
    }
  }

  /**
   * Check push permission status
   */
  async checkPushPermission(): Promise<void> {
    const permission = this.pushController.getPermission();
    const subscription = await this.pushController.getSubscription();
    const enabled = permission === 'granted' && this.state.pushSupported && subscription !== null;

    this.replaceState({
      pushPermission: permission,
      pushEnabled: enabled,
    });

    this.notifyListeners();
  }
}
